<template>
  <div class="critical-mineral-dashboard">
    <div class="critical-layout">
      <aside class="critical-sidebar">
        <v-card class="filter-card" outlined>
          <v-card-title class="filter-card__title">
            Critical Mineral
          </v-card-title>
          <v-card-text class="filter-card__body">
            <v-select
              v-model="selectedCategory"
              :items="categories"
              label="Category"
              dense
              outlined
              hide-details="auto"
              class="mb-3"
            />

            <v-select
              v-if="showMineralType"
              v-model="selectedMineralType"
              :items="availableMineralTypes"
              label="Mineral Type"
              dense
              outlined
              hide-details="auto"
              class="mb-3"
            />

            <v-select
              v-if="showMetricForm"
              v-model="selectedMetricForm"
              :items="availableMetricForms"
              label="Material Form"
              dense
              outlined
              hide-details="auto"
              class="mb-3"
            />

            <div v-if="showDisplayModeToggle" class="display-toggle">
              <div class="filter-label">Display</div>
              <v-btn-toggle
                v-model="selectedDisplayMode"
                mandatory
                dense
                class="display-toggle__buttons"
              >
                <v-btn value="value" small>Value</v-btn>
                <v-btn value="percentage" small>Percentage</v-btn>
              </v-btn-toggle>
            </div>

            <v-alert
              v-if="dashboardNotes.mineral_cost && selectedCategory === 'Mineral Cost'"
              type="info"
              dense
              outlined
              class="mt-4 mb-0"
            >
              {{ dashboardNotes.mineral_cost }}
            </v-alert>
          </v-card-text>
        </v-card>

        <v-card v-if="selectedCountry" class="selected-country-card" outlined>
          <v-card-title class="selected-country-card__title">
            {{ selectedCountry.country }}
          </v-card-title>
          <v-card-text class="selected-country-card__body">
            <div class="selected-country-card__metric">
              {{ formatDisplayValue(selectedCountry.display_value, selectedCountry.unit, selectedCategory === 'Automotive Manufacturing Status') }}
            </div>
            <div v-if="selectedCountry.region" class="selected-country-card__meta">
              Region: {{ selectedCountry.region }}
            </div>
            <div v-if="selectedCountry.year" class="selected-country-card__meta">
              Year: {{ selectedCountry.year }}
            </div>
            <div v-if="selectedCountry.source_file" class="selected-country-card__meta">
              Source: {{ selectedCountry.source_file }}
            </div>
          </v-card-text>
        </v-card>

        <v-card class="chart-card" outlined>
          <v-card-title class="chart-card__title">{{ chartTitle }}</v-card-title>
          <v-card-text>
            <div v-if="loadingDashboard" class="chart-placeholder">
              <v-progress-circular indeterminate color="primary" size="36" />
            </div>
            <div v-else-if="!chartRows.length" class="chart-placeholder">
              No chart data available.
            </div>
            <div
              v-else
              class="chart-canvas-wrap"
              :style="{ height: `${chartCanvasHeight}px` }"
            >
              <canvas ref="topCountriesChart"></canvas>
            </div>
          </v-card-text>
        </v-card>
      </aside>

      <section class="critical-map-panel">
        <v-card class="map-shell" outlined>
          <v-card-text class="map-shell__body">
            <div v-if="loadingMap || loadingDashboard" class="map-placeholder">
              <v-progress-circular indeterminate color="primary" size="56" />
              <p>Loading critical mineral data...</p>
            </div>
            <div v-else-if="mapError" class="map-placeholder">
              <v-icon size="48" color="error">mdi-alert-circle</v-icon>
              <p>{{ mapError }}</p>
            </div>
            <div v-else class="map-stage">
              <div class="map-stage__header">
                <div class="map-stage__heading">
                  {{ selectedCategory }}
                  <span
                    v-if="selectedMineralType || resolvedUnitLabel"
                    class="map-stage__heading-detail"
                  >
                    ({{ selectedMineralType || resolvedUnitLabel }})
                  </span>
                </div>
              </div>
                <l-map
                  ref="leafletMap"
                  :zoom="zoom"
                  :center="center"
                  :options="mapOptions"
                  class="critical-leaflet-map"
                >
                  <l-control-zoom position="bottomleft" />
                  <l-tile-layer
                    :url="tileUrl"
                    :attribution="attribution"
                    :options="tileOptions"
                  />
                <l-geo-json
                  v-if="geojsonData"
                  :geojson="geojsonData"
                  :options="geojsonOptions"
                />
              </l-map>

              <v-card class="critical-legend" outlined>
                <div class="critical-legend__title">Legend</div>
                <div v-if="selectedCategory === 'Automotive Manufacturing Status'" class="critical-legend__items">
                  <div class="critical-legend__item">
                    <span class="critical-legend__swatch" :style="{ backgroundColor: '#7CB342' }"></span>
                    YES
                  </div>
                  <div class="critical-legend__item">
                    <span class="critical-legend__swatch" :style="{ backgroundColor: '#EF5350' }"></span>
                    NO
                  </div>
                  <div class="critical-legend__item">
                    <span class="critical-legend__swatch" :style="{ backgroundColor: '#ECEFF1' }"></span>
                    No Data
                  </div>
                </div>
                <div v-else class="critical-legend__items">
                  <div class="critical-legend__item">
                    <span class="critical-legend__swatch" :style="{ backgroundColor: '#BDD9F8' }"></span>
                    Lower
                  </div>
                  <div class="critical-legend__item">
                    <span class="critical-legend__swatch" :style="{ backgroundColor: '#2F80ED' }"></span>
                    Higher
                  </div>
                  <div class="critical-legend__item">
                    <span class="critical-legend__swatch" :style="{ backgroundColor: '#ECEFF1' }"></span>
                    No Data
                  </div>
                </div>
              </v-card>

              <div
                v-if="hoveredCountry"
                class="critical-tooltip"
                :style="{ left: `${tooltipX}px`, top: `${tooltipY}px` }"
              >
                {{ hoveredCountry.name }}:
                {{ hoveredCountry.valueLabel }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </section>
    </div>
  </div>
</template>

<script>
import { LMap, LTileLayer, LGeoJson, LControlZoom } from 'vue2-leaflet'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  PieController,
} from 'chart.js'
import { loadJsonFromApi, loadWorldGeojson } from './worldMapLoader'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  PieController
)

delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

const COUNTRY_EQUIVALENTS = {
  usa: ['United States of America', 'United States', 'USA', 'US'],
  uk: ['United Kingdom', 'UK', 'Britain'],
  drc: ['DRC', 'Democratic Republic of Congo', 'Democratic Republic of the Congo'],
  czechia: ['Czechia', 'Czech Republic'],
  southkorea: ['South Korea', 'Republic of Korea', 'Korea, Republic of'],
  northkorea: ['North Korea', "Dem. Rep. Korea", "Korea, Democratic People's Republic of"],
  russia: ['Russia', 'Russian Federation'],
  turkey: ['Turkey', 'Turkiye'],
  taiwanprovinceofchina: ['Taiwan, Province of China', 'Taiwan'],
}

export default {
  name: 'CriticalMineral',
  components: {
    LMap,
    LTileLayer,
    LGeoJson,
    LControlZoom,
  },
  data() {
    return {
      loadingMap: true,
      loadingMetadata: true,
      loadingDashboard: true,
      mapError: null,
      metadata: null,
      dashboard: null,
      geojsonData: null,
      hoveredCountry: null,
      tooltipX: 0,
      tooltipY: 0,
      selectedCategory: 'Mineral Reserves',
      selectedMineralType: null,
      selectedMetricForm: null,
      selectedDisplayMode: 'value',
      selectedCountry: null,
      topCountriesChartInstance: null,
      zoom: 2,
      center: [24, 8],
      tileUrl: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      tileOptions: { maxZoom: 18, subdomains: 'abcd' },
      mapOptions: {
        zoomControl: false,
        scrollWheelZoom: true,
        dragging: true,
        minZoom: 1,
        maxZoom: 10,
      },
    }
  },
  computed: {
    categories() {
      return this.metadata?.categories || []
    },
    categoryFilters() {
      return this.metadata?.category_filters || {}
    },
    currentCategoryFilter() {
      return this.categoryFilters[this.selectedCategory] || { mineral_types: [], years: [], metric_forms: [] }
    },
    categoryRules() {
      return this.metadata?.category_rules || {}
    },
    currentRule() {
      return this.categoryRules[this.selectedCategory] || {}
    },
    availableMineralTypes() {
      return this.currentCategoryFilter.mineral_types || []
    },
    availableMetricForms() {
      return this.dashboard?.available_metric_forms || this.currentCategoryFilter.metric_forms || []
    },
    showMineralType() {
      return this.currentRule.show_mineral_type && this.availableMineralTypes.length > 0
    },
    showMetricForm() {
      return this.selectedCategory === 'Mineral Cost' && this.availableMetricForms.length > 0
    },
    showDisplayModeToggle() {
      return ['Mineral Reserves', 'Mineral Production', 'Battery Production Capacity'].includes(this.selectedCategory)
    },
    rows() {
      return this.dashboard?.rows || []
    },
    chartRows() {
      return [...this.rows].sort((a, b) => {
        const aValue = typeof a.display_value === 'number' ? a.display_value : -Infinity
        const bValue = typeof b.display_value === 'number' ? b.display_value : -Infinity
        return bValue - aValue
      })
    },
    chartTitle() {
      const unitSuffix = this.resolvedUnitLabel ? ` (${this.resolvedUnitLabel})` : ''
      return this.selectedCategory === 'Automotive Manufacturing Status'
        ? 'Automotive Manufacturing Split'
        : `All Countries${unitSuffix}`
    },
    dashboardNotes() {
      return this.dashboard?.notes || this.metadata?.notes || {}
    },
    resolvedUnitLabel() {
      return this.dashboard?.unit || ''
    },
    chartCanvasHeight() {
      if (this.selectedCategory === 'Automotive Manufacturing Status') {
        return 320
      }
      return Math.max(360, this.chartRows.length * 28)
    },
    geojsonOptions() {
      return {
        style: this.getFeatureStyle,
        onEachFeature: this.onEachFeature,
      }
    },
    mapRowsByKey() {
      const index = {}
      this.rows.forEach(row => {
        (row.country_keys || []).forEach(key => {
          const normalizedKey = this.normalizeCountryKeySimple(key)
          if (normalizedKey && !index[normalizedKey]) index[normalizedKey] = row
        })
      })
      return index
    },
    valueExtent() {
      const values = this.rows
        .map(row => row.display_value)
        .filter(value => typeof value === 'number' && !Number.isNaN(value))
      if (!values.length) return { min: null, max: null }
      return { min: Math.min(...values), max: Math.max(...values) }
    },
  },
  watch: {
    selectedCategory() {
      this.resetSelectionForCategory()
      this.loadDashboard()
    },
    selectedMineralType() {
      this.loadDashboard()
    },
    selectedMetricForm() {
      this.loadDashboard()
    },
    selectedDisplayMode() {
      this.loadDashboard()
    },
    dashboard() {
      this.selectedCountry = null
      this.$nextTick(() => {
        this.renderCharts()
      })
    },
  },
  async mounted() {
    await Promise.all([this.loadMetadata(), this.loadGeojson()])
    this.initializeDefaults()
    await this.loadDashboard()
  },
  beforeDestroy() {
    this.destroyCharts()
  },
  methods: {
    normalizeCountryKey(value) {
      if (!value) return ''
      const compact = this.normalizeCountryKeySimple(value)
      const aliases = Object.values(COUNTRY_EQUIVALENTS).find(list =>
        list.some(item => this.normalizeCountryKeySimple(item) === compact)
      )
      return aliases ? this.normalizeCountryKeySimple(aliases[0]) : compact
    },
    normalizeCountryKeySimple(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '')
    },
    async loadMetadata() {
      this.loadingMetadata = true
      try {
        this.metadata = await loadJsonFromApi(this.$domain, '/data/critical-mineral/metadata')
      } catch (error) {
        this.mapError = error.message
      } finally {
        this.loadingMetadata = false
      }
    },
    async loadGeojson() {
      this.loadingMap = true
      try {
        this.geojsonData = await loadWorldGeojson(this.$domain)
      } catch (error) {
        this.mapError = error.message
      } finally {
        this.loadingMap = false
      }
    },
    initializeDefaults() {
      if (!this.categories.length) return
      this.selectedCategory = this.categories[0]
      this.resetSelectionForCategory()
    },
    resetSelectionForCategory() {
      const mineralTypes = this.availableMineralTypes
      this.selectedMineralType = mineralTypes.length ? mineralTypes[0] : null

      const forms = this.currentCategoryFilter.metric_forms || []
      this.selectedMetricForm = forms.length ? forms[0] : null

      if (this.selectedCategory === 'Mineral Processing/Refinery Capacity') {
        this.selectedDisplayMode = 'percentage'
      } else if (!this.showDisplayModeToggle) {
        this.selectedDisplayMode = 'value'
      }
    },
    async loadDashboard() {
      if (this.loadingMetadata || !this.selectedCategory) return
      this.loadingDashboard = true
      try {
        const params = new URLSearchParams()
        params.set('category', this.selectedCategory)
        if (this.showMineralType && this.selectedMineralType) {
          params.set('mineral_type', this.selectedMineralType)
        }
        if (this.showMetricForm && this.selectedMetricForm) {
          params.set('metric_form', this.selectedMetricForm)
        }
        params.set('display_mode', this.selectedDisplayMode)

        this.dashboard = await loadJsonFromApi(this.$domain, `/data/critical-mineral/dashboard-data?${params.toString()}`)
      } catch (error) {
        this.mapError = error.message
      } finally {
        this.loadingDashboard = false
      }
    },
    destroyCharts() {
      if (this.topCountriesChartInstance) {
        this.topCountriesChartInstance.destroy()
        this.topCountriesChartInstance = null
      }
    },
    renderCharts() {
      this.destroyCharts()
      if (!this.$refs.topCountriesChart) return

      if (this.chartRows.length) {
        if (this.selectedCategory === 'Automotive Manufacturing Status') {
          const yesCount = this.chartRows.filter(row => row.display_value >= 1).length
          const noCount = this.chartRows.filter(row => row.display_value < 1).length
          this.topCountriesChartInstance = new ChartJS(this.$refs.topCountriesChart.getContext('2d'), {
            type: 'pie',
            data: {
              labels: ['YES', 'NO'],
              datasets: [{
                data: [yesCount, noCount],
                backgroundColor: ['#7CB342', '#EF5350'],
                borderWidth: 0,
              }],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                },
                tooltip: {
                  callbacks: {
                    label: context => `${context.label}: ${context.parsed}`,
                  },
                },
              },
            },
          })
        } else {
          this.topCountriesChartInstance = new ChartJS(this.$refs.topCountriesChart.getContext('2d'), {
            type: 'bar',
            data: {
              labels: this.chartRows.map(row => row.country),
              datasets: [{
                label: this.selectedCategory,
                data: this.chartRows.map(row => row.display_value),
                backgroundColor: '#2F80ED',
                borderRadius: 6,
              }],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: context => this.formatDisplayValue(context.parsed.x, this.resolvedUnitLabel),
                  },
                },
              },
              scales: {
                x: {
                  title: {
                    display: !!this.resolvedUnitLabel,
                    text: this.resolvedUnitLabel,
                  },
                  ticks: {
                    callback: value => this.formatAxisTick(value),
                  },
                },
                y: {
                  ticks: {
                    autoSkip: false,
                  },
                },
              },
              indexAxis: 'y',
            },
          })
        }
      }
    },
    formatAxisTick(value) {
      if (value === null || value === undefined || Number.isNaN(value)) return 'No data'
      if (this.selectedCategory === 'Automotive Manufacturing Status') {
        return value >= 1 ? 'YES' : 'NO'
      }
      if (this.resolvedUnitLabel && this.resolvedUnitLabel.includes('%')) {
        return `${value.toFixed(1)}%`
      }
      if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`
      if (Math.abs(value) >= 1000) return value.toLocaleString()
      return Number(value).toFixed(2).replace(/\.00$/, '')
    },
    formatDisplayValue(value, unit, isStatus = false) {
      if (isStatus) {
        if (value === 1) return 'YES'
        if (value === 0) return 'NO'
        return '--'
      }
      if (value === null || value === undefined || Number.isNaN(value)) return '--'
      if (unit && String(unit).includes('%')) return `${Number(value).toFixed(1)}%`
      const formatted = Math.abs(value) >= 1000
        ? Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })
        : Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
      return unit && unit !== 'status' ? `${formatted} ${unit}` : formatted
    },
    lookupRowForFeature(feature) {
      const properties = feature.properties || {}
      const candidates = [
        properties.name,
        properties.iso3,
        properties.iso_3166_1_alpha_2_codes,
      ]
      for (const candidate of candidates) {
        const key = this.normalizeCountryKey(candidate)
        if (key && this.mapRowsByKey[key]) return this.mapRowsByKey[key]
      }
      return null
    },
    getFeatureStyle(feature) {
      const row = this.lookupRowForFeature(feature)
      const fillColor = this.getFillColor(row ? row.display_value : null)
      return {
        fillColor,
        weight: 1,
        opacity: 1,
        color: '#CFD8DC',
        fillOpacity: row ? 0.82 : 0.55,
      }
    },
    getFillColor(value) {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return '#ECEFF1'
      }
      if (this.selectedCategory === 'Automotive Manufacturing Status') {
        return value >= 1 ? '#7CB342' : '#EF5350'
      }
      const { min, max } = this.valueExtent
      if (min === null || max === null) return '#DCEBFA'
      if (max === min) return '#2F80ED'
      const ratio = (value - min) / (max - min)
      if (ratio >= 0.8) return '#1F5FBF'
      if (ratio >= 0.6) return '#2F80ED'
      if (ratio >= 0.4) return '#5EA2F5'
      if (ratio >= 0.2) return '#92C3F8'
      return '#BDD9F8'
    },
    onEachFeature(feature, layer) {
      layer.on({
        mouseover: event => this.handleMouseOver(event, feature),
        mousemove: event => this.handleMouseMove(event, feature),
        mouseout: () => this.handleMouseOut(layer, feature),
        click: () => this.handleFeatureClick(feature),
      })
    },
    handleMouseOver(event, feature) {
      const row = this.lookupRowForFeature(feature)
      this.hoveredCountry = {
        name: feature.properties?.name || 'Unknown',
        valueLabel: row
          ? this.formatDisplayValue(row.display_value, row.unit, this.selectedCategory === 'Automotive Manufacturing Status')
          : 'No Data',
      }
      this.tooltipX = event.originalEvent.clientX + 12
      this.tooltipY = event.originalEvent.clientY - 12
      event.target.setStyle({ weight: 2, color: '#607D8B' })
    },
    handleMouseMove(event, feature) {
      if (!this.hoveredCountry) return
      this.hoveredCountry.name = feature.properties?.name || 'Unknown'
      this.tooltipX = event.originalEvent.clientX + 12
      this.tooltipY = event.originalEvent.clientY - 12
    },
    handleMouseOut(layer, feature) {
      this.hoveredCountry = null
      layer.setStyle(this.getFeatureStyle(feature))
    },
    handleFeatureClick(feature) {
      const row = this.lookupRowForFeature(feature)
      if (row) {
        this.selectedCountry = row
      } else {
        this.selectedCountry = {
          country: feature.properties?.name || 'Unknown',
          display_value: null,
          unit: null,
        }
      }
    },
  },
}
</script>

<style scoped>
.critical-mineral-dashboard {
  padding: 24px;
  height: calc(100vh - 88px);
  overflow: hidden;
  box-sizing: border-box;
}

.critical-layout {
  display: grid;
  grid-template-columns: 420px minmax(0, 1fr);
  gap: 20px;
  align-items: stretch;
  height: 100%;
}

.critical-sidebar {
  display: grid;
  gap: 16px;
  align-content: start;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
}

.filter-card__title,
.chart-card__title {
  font-size: 1.05rem;
  font-weight: 700;
}

.filter-card__body {
  padding-top: 8px;
}

.display-toggle {
  margin-top: 6px;
}

.filter-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #546e7a;
  margin-bottom: 8px;
}

.display-toggle__buttons {
  width: 100%;
}

.display-toggle__buttons .v-btn {
  flex: 1 1 0;
}

.selected-country-card__title {
  padding-bottom: 0;
  font-size: 1rem;
  font-weight: 700;
}

.selected-country-card__body {
  padding-top: 8px;
}

.selected-country-card__metric {
  font-size: 1.15rem;
  font-weight: 700;
  color: #2f80ed;
  margin-bottom: 8px;
}

.selected-country-card__meta {
  font-size: 0.85rem;
  color: #607d8b;
}

.chart-card {
  padding-bottom: 8px;
}

.chart-placeholder {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #607d8b;
}

.chart-canvas-wrap {
  position: relative;
  width: 100%;
  min-height: 360px;
}

.critical-map-panel {
  min-height: 0;
  height: 100%;
}

.map-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.map-shell__body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px;
  overflow: hidden;
}

.map-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #607d8b;
}

.map-stage {
  position: relative;
  height: 100%;
  min-height: 0;
}

.map-stage__header {
  position: absolute;
  top: 8px;
  left: 14px;
  z-index: 1000;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(207, 216, 220, 0.9);
  border-radius: 8px;
}

.map-stage__heading {
  font-size: 1rem;
  font-weight: 700;
  color: #263238;
}

.map-stage__heading-detail {
  font-size: 0.85rem;
  font-weight: 600;
  color: #607d8b;
}

.critical-leaflet-map {
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.critical-legend {
  position: absolute;
  right: 20px;
  bottom: 20px;
  z-index: 1000;
  padding: 12px 14px;
  min-width: 150px;
  max-width: 180px;
  background: rgba(255, 255, 255, 0.96);
}

.critical-legend__title {
  font-size: 0.84rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.critical-legend__items {
  display: grid;
  gap: 6px;
}

.critical-legend__item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #455a64;
}

.critical-legend__swatch {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: 1px solid rgba(38, 50, 56, 0.12);
}

.critical-tooltip {
  position: fixed;
  z-index: 1200;
  pointer-events: none;
  padding: 8px 10px;
  background: rgba(38, 50, 56, 0.92);
  color: #fff;
  border-radius: 8px;
  font-size: 0.82rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
}

@media (max-width: 1260px) {
  .critical-layout {
    grid-template-columns: 360px minmax(0, 1fr);
  }
}

@media (max-width: 980px) {
  .critical-mineral-dashboard {
    padding: 16px;
    height: auto;
    overflow: visible;
  }

  .critical-layout {
    grid-template-columns: 1fr;
    height: auto;
  }

  .critical-sidebar {
    height: auto;
    overflow: visible;
    padding-right: 0;
  }

  .critical-map-panel {
    height: auto;
  }

  .map-shell {
    height: auto;
  }

  .map-shell__body {
    height: 620px;
    min-height: 620px;
  }
}

@media (max-width: 640px) {
  .map-shell__body {
    height: 500px;
    min-height: 500px;
    padding: 8px;
  }

  .critical-legend {
    right: 12px;
    bottom: 12px;
    left: auto;
    min-width: 138px;
    max-width: 150px;
  }
}
</style>
